import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, Loader2, Minus, Plus, Trash2, Shield, CreditCard, Lock, Truck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import GlossyIcon from "@/components/GlossyIcon";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import FloatingInput from "@/components/FloatingInput";
import CheckoutStepper, { CHECKOUT_STEPS } from "@/components/CheckoutStepper";
import { useCart } from "@/contexts/CartContext";
import { getFirstImage, getEffectivePrice } from "@/hooks/useProducts";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useShippingTiers, useShippingSettings, calculateShipping } from "@/hooks/useShipping";
import FreeShippingBar from "@/components/FreeShippingBar";
import CartUpsell from "@/components/CartUpsell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/* ── Auto-format helpers ── */
const formatPostcode = (val: string) => {
  const clean = val.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  if (clean.length > 4) {
    return clean.slice(0, -3) + " " + clean.slice(-3);
  }
  return clean;
};

const formatPhone = (val: string) => {
  const digits = val.replace(/\D/g, "");
  if (digits.length <= 5) return digits;
  if (digits.length <= 11) return digits.slice(0, 5) + " " + digits.slice(5);
  return digits.slice(0, 5) + " " + digits.slice(5, 11);
};

/* ── Animation variants ── */
const stepVariants = {
  enter: { opacity: 0, x: 40, filter: "blur(4px)" },
  center: { opacity: 1, x: 0, filter: "blur(0px)" },
  exit: { opacity: 0, x: -40, filter: "blur(4px)" },
};

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className={`bg-card/60 backdrop-blur-xl border border-border/40 rounded-2xl p-6 shadow-[0_8px_32px_hsl(var(--primary)/0.04)] ${className}`}
  >
    {children}
  </motion.div>
);

const Checkout = () => {
  const { items, subtotal, clearCart, totalItems, updateQuantity, removeItem } = useCart();
  const navigate = useNavigate();
  const [lastItemAction, setLastItemAction] = useState<(() => void) | null>(null);
  const [searchParams] = useSearchParams();
  const [submitting, setSubmitting] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState<"guest" | "account">("guest");
  const [shipToDifferent, setShipToDifferent] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [discountCode, setDiscountCode] = useState(() => {
    const saved = localStorage.getItem("plym_welcome_code");
    if (saved) {
      localStorage.removeItem("plym_welcome_code");
      return saved;
    }
    return "";
  });
  const [discountApplied, setDiscountApplied] = useState<{
    code: string;
    type: string;
    value: number;
    min_order: number | null;
  } | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("cancelled") === "true") {
      toast.error("Payment was cancelled. You can try again.");
    }
  }, [searchParams]);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    postcode: "",
    county: "",
    notes: "",
    shipFirstName: "",
    shipLastName: "",
    shipAddress1: "",
    shipAddress2: "",
    shipCity: "",
    shipPostcode: "",
    shipCounty: "",
  });

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const { data: tiers = [] } = useShippingTiers();
  const { data: shippingSettings } = useShippingSettings();
  const freeThreshold = shippingSettings?.free_shipping_threshold ?? 50;

  const cartWeightKg = useMemo(
    () =>
      items.reduce(
        (sum, item) => sum + (item.product.weight_kg ?? 0) * item.quantity,
        0
      ),
    [items]
  );

  const { cost: shipping, tierLabel: shippingLabel, isFree: isFreeShipping } = useMemo(
    () => calculateShipping(subtotal, cartWeightKg, tiers, freeThreshold),
    [subtotal, cartWeightKg, tiers, freeThreshold]
  );

  const discountAmount = useMemo(() => {
    if (!discountApplied) return 0;
    if (discountApplied.min_order && subtotal < discountApplied.min_order) return 0;
    if (discountApplied.type === "percentage") {
      return Math.round(subtotal * (discountApplied.value / 100) * 100) / 100;
    }
    return Math.min(discountApplied.value, subtotal);
  }, [discountApplied, subtotal]);

  const total = subtotal - discountAmount + shipping;

  const applyDiscount = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) return;
    setDiscountLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("validate-discount", {
        body: { code, subtotal },
      });

      if (error || !data || !data.valid) {
        toast.error((data && data.error) || "Invalid discount code");
        return;
      }

      setDiscountApplied({
        code: data.code,
        type: data.type,
        value: data.value,
        min_order: data.min_order,
      });
      toast.success(`Discount "${data.code}" applied!`);
    } catch {
      toast.error("Failed to validate discount code");
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;

    setSubmitting(true);
    try {
      const billingAddress = [
        form.address1,
        form.address2,
        form.city,
        form.county,
        form.postcode,
      ]
        .filter(Boolean)
        .join("\n");

      const shippingAddress = shipToDifferent
        ? [
            `${form.shipFirstName} ${form.shipLastName}`.trim(),
            form.shipAddress1,
            form.shipAddress2,
            form.shipCity,
            form.shipCounty,
            form.shipPostcode,
          ]
            .filter(Boolean)
            .join("\n")
        : billingAddress;

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          customerName: `${form.firstName} ${form.lastName}`.trim(),
          customerEmail: form.email,
          shippingAddress,
          subtotal,
          shipping,
          total,
          discountCode: discountApplied?.code || null,
          notes: form.notes || null,
          items: items.map((item) => ({
            productId: item.product.id,
            name: item.product.name,
            image: getFirstImage(item.product.images),
            variantLabel: item.variantLabel || null,
            quantity: item.quantity,
            price: getEffectivePrice(item.product),
          })),
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        if (discountApplied) {
          localStorage.setItem("plym_order_discount", JSON.stringify({
            code: discountApplied.code,
            amount: discountAmount,
          }));
        }
        // Redirect in the same tab to avoid popup blockers. Cart is cleared
        // on the order confirmation page after Stripe verifies the payment.
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error(err);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Step validation ── */
  const canProceedStep0 = form.firstName && form.lastName && form.email && form.address1 && form.city && form.postcode;
  const canProceedStep1 = !shipToDifferent || (form.shipFirstName && form.shipLastName && form.shipAddress1 && form.shipCity && form.shipPostcode);

  const stepperRef = useRef<HTMLDivElement>(null);

  const scrollToStepper = () => {
    setTimeout(() => {
      stepperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const nextStep = () => {
    if (currentStep === 0 && !canProceedStep0) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (currentStep === 1 && !canProceedStep1) {
      toast.error("Please fill in all shipping fields");
      return;
    }
    setCurrentStep((s) => Math.min(s + 1, 2));
    scrollToStepper();
  };

  const prevStep = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
    scrollToStepper();
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <SEO title="Checkout" description="Securely complete your Plymhair order." url="/checkout" noindex />
        <Ticker />
        <Navbar />
        <div className="flex-1 flex items-center justify-center flex-col gap-4 py-20">
          <GlossyIcon name="cart" size={48} className="opacity-40" />
          <p className="text-muted-foreground font-body">Your cart is empty</p>
          <Link to="/shop">
            <Button>Continue Shopping</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEO title="Checkout" description="Securely complete your Plymhair order." url="/checkout" noindex />
      <Ticker />
      <Navbar />

      <div className="container py-8 flex-1">
        <Link
          to="/shop"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6 font-body transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Continue Shopping
        </Link>

        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-display text-2xl md:text-3xl font-bold mb-6"
        >
          Checkout
        </motion.h1>

        {/* Guest / Account toggle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-2 mb-8 p-1.5 bg-muted/60 backdrop-blur-sm rounded-xl border border-border/30"
        >
          <button
            type="button"
            onClick={() => setCheckoutMode("guest")}
            className={`relative flex-1 py-2.5 text-sm font-body font-semibold rounded-lg transition-all duration-300 ${
              checkoutMode === "guest"
                ? "bg-background text-foreground shadow-md"
                : "bg-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Guest Checkout
          </button>
          <button
            type="button"
            onClick={() => {
              setCheckoutMode("account");
              navigate("/auth?redirect=/checkout");
            }}
            className={`relative flex-1 py-2.5 text-sm font-body font-semibold rounded-lg transition-all duration-300 ${
              checkoutMode === "account"
                ? "bg-background text-foreground shadow-md"
                : "bg-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Sign In / Create Account
          </button>
        </motion.div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Left: Stepper Form */}
            <div className="lg:col-span-2">
              <div ref={stepperRef} className="scroll-mt-24">
                <CheckoutStepper currentStep={currentStep} steps={CHECKOUT_STEPS} />
              </div>

              <AnimatePresence mode="wait">
                {/* ── Step 0: Billing Details ── */}
                {currentStep === 0 && (
                  <motion.div
                    key="step-0"
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="space-y-6"
                  >
                    <GlassCard>
                      <h2 className="font-display text-lg font-semibold mb-5 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-primary">1</span>
                        </div>
                        Your Details
                      </h2>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <FloatingInput
                          label="First Name *"
                          required
                          value={form.firstName}
                          onChange={(e) => update("firstName", e.target.value)}
                          maxLength={100}
                        />
                        <FloatingInput
                          label="Last Name *"
                          required
                          value={form.lastName}
                          onChange={(e) => update("lastName", e.target.value)}
                          maxLength={100}
                        />
                        <FloatingInput
                          label="Email *"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          maxLength={255}
                        />
                        <FloatingInput
                          label="Phone"
                          type="tel"
                          value={form.phone}
                          onChange={(e) => update("phone", formatPhone(e.target.value))}
                          maxLength={14}
                        />
                      </div>
                    </GlassCard>

                    <GlassCard>
                      <h2 className="font-display text-lg font-semibold mb-5 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-xs font-bold text-primary">2</span>
                        </div>
                        Billing Address
                      </h2>
                      <div className="space-y-4">
                        <FloatingInput
                          label="Address Line 1 *"
                          required
                          value={form.address1}
                          onChange={(e) => update("address1", e.target.value)}
                          maxLength={200}
                        />
                        <FloatingInput
                          label="Address Line 2"
                          value={form.address2}
                          onChange={(e) => update("address2", e.target.value)}
                          maxLength={200}
                        />
                        <div className="grid sm:grid-cols-3 gap-4">
                          <FloatingInput
                            label="City *"
                            required
                            value={form.city}
                            onChange={(e) => update("city", e.target.value)}
                            maxLength={100}
                          />
                          <FloatingInput
                            label="County"
                            value={form.county}
                            onChange={(e) => update("county", e.target.value)}
                            maxLength={100}
                          />
                          <FloatingInput
                            label="Postcode *"
                            required
                            value={form.postcode}
                            onChange={(e) => update("postcode", formatPostcode(e.target.value))}
                            maxLength={8}
                          />
                        </div>
                      </div>
                    </GlassCard>

                    <div className="flex justify-end pt-2">
                      <Button
                        type="button"
                        onClick={nextStep}
                        size="lg"
                        className="px-8 rounded-xl shadow-[0_4px_20px_hsl(var(--primary)/0.25)] hover:shadow-[0_6px_28px_hsl(var(--primary)/0.35)] transition-shadow"
                      >
                        Continue to Shipping
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* ── Step 1: Shipping ── */}
                {currentStep === 1 && (
                  <motion.div
                    key="step-1"
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="space-y-6"
                  >
                    <GlassCard>
                      <h2 className="font-display text-lg font-semibold mb-5 flex items-center gap-2">
                        <Truck className="w-5 h-5 text-primary" />
                        Shipping Address
                      </h2>

                      <button
                        type="button"
                        onClick={() => setShipToDifferent((prev) => !prev)}
                        className="flex items-center gap-3 cursor-pointer group mb-4"
                      >
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-300 ${
                            shipToDifferent
                              ? "bg-primary border-primary"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          {shipToDifferent && (
                            <motion.svg
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-3 h-3 text-primary-foreground"
                              viewBox="0 0 12 12"
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </motion.svg>
                          )}
                        </div>
                        <span className="font-body text-sm font-medium group-hover:text-primary transition-colors">
                          Ship to a different address
                        </span>
                      </button>

                      <AnimatePresence>
                        {shipToDifferent && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-4 pt-2 pl-4 border-l-2 border-primary/20">
                              <div className="grid sm:grid-cols-2 gap-4">
                                <FloatingInput
                                  label="First Name *"
                                  required={shipToDifferent}
                                  value={form.shipFirstName}
                                  onChange={(e) => update("shipFirstName", e.target.value)}
                                  maxLength={100}
                                />
                                <FloatingInput
                                  label="Last Name *"
                                  required={shipToDifferent}
                                  value={form.shipLastName}
                                  onChange={(e) => update("shipLastName", e.target.value)}
                                  maxLength={100}
                                />
                              </div>
                              <FloatingInput
                                label="Address Line 1 *"
                                required={shipToDifferent}
                                value={form.shipAddress1}
                                onChange={(e) => update("shipAddress1", e.target.value)}
                                maxLength={200}
                              />
                              <FloatingInput
                                label="Address Line 2"
                                value={form.shipAddress2}
                                onChange={(e) => update("shipAddress2", e.target.value)}
                                maxLength={200}
                              />
                              <div className="grid sm:grid-cols-3 gap-4">
                                <FloatingInput
                                  label="City *"
                                  required={shipToDifferent}
                                  value={form.shipCity}
                                  onChange={(e) => update("shipCity", e.target.value)}
                                  maxLength={100}
                                />
                                <FloatingInput
                                  label="County"
                                  value={form.shipCounty}
                                  onChange={(e) => update("shipCounty", e.target.value)}
                                  maxLength={100}
                                />
                                <FloatingInput
                                  label="Postcode *"
                                  required={shipToDifferent}
                                  value={form.shipPostcode}
                                  onChange={(e) => update("shipPostcode", formatPostcode(e.target.value))}
                                  maxLength={8}
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {!shipToDifferent && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="p-4 rounded-xl bg-muted/40 border border-border/30"
                        >
                          <p className="text-sm text-muted-foreground font-body">
                            Shipping to your billing address
                          </p>
                          {form.address1 && (
                            <p className="text-sm font-body mt-1 text-foreground">
                              {form.address1}, {form.city} {form.postcode}
                            </p>
                          )}
                        </motion.div>
                      )}
                    </GlassCard>

                    <div className="flex justify-between pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={prevStep}
                        className="rounded-xl"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" /> Back
                      </Button>
                      <Button
                        type="button"
                        onClick={nextStep}
                        size="lg"
                        className="px-8 rounded-xl shadow-[0_4px_20px_hsl(var(--primary)/0.25)] hover:shadow-[0_6px_28px_hsl(var(--primary)/0.35)] transition-shadow"
                      >
                        Continue to Review
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* ── Step 2: Review & Notes ── */}
                {currentStep === 2 && (
                  <motion.div
                    key="step-2"
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="space-y-6"
                  >
                    {/* Review summary cards */}
                    <GlassCard>
                      <h2 className="font-display text-lg font-semibold mb-4">Review Your Details</h2>
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="space-y-1">
                          <p className="text-xs font-body font-semibold text-muted-foreground uppercase tracking-wider">Billing</p>
                          <p className="text-sm font-body font-medium">{form.firstName} {form.lastName}</p>
                          <p className="text-sm font-body text-muted-foreground">{form.email}</p>
                          {form.phone && <p className="text-sm font-body text-muted-foreground">{form.phone}</p>}
                          <p className="text-sm font-body text-muted-foreground">{form.address1}</p>
                          <p className="text-sm font-body text-muted-foreground">{form.city}, {form.postcode}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-xs font-body font-semibold text-muted-foreground uppercase tracking-wider">Shipping to</p>
                          {shipToDifferent ? (
                            <>
                              <p className="text-sm font-body font-medium">{form.shipFirstName} {form.shipLastName}</p>
                              <p className="text-sm font-body text-muted-foreground">{form.shipAddress1}</p>
                              <p className="text-sm font-body text-muted-foreground">{form.shipCity}, {form.shipPostcode}</p>
                            </>
                          ) : (
                            <p className="text-sm font-body text-muted-foreground">Same as billing</p>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentStep(0)}
                        className="text-xs text-primary hover:text-primary/80 font-body font-semibold mt-3 transition-colors"
                      >
                        Edit details
                      </button>
                    </GlassCard>

                    <GlassCard>
                      <h2 className="font-display text-lg font-semibold mb-4">Order Notes</h2>
                      <textarea
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        placeholder="Any special instructions…"
                        maxLength={500}
                        className="w-full border border-border/40 rounded-xl px-4 py-3 text-sm font-body bg-background/60 backdrop-blur-sm focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none min-h-[100px] resize-y transition-all"
                      />
                    </GlassCard>

                    {/* Trust badges */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center justify-center gap-6 py-4"
                    >
                      {[
                        { icon: Shield, label: "Secure Checkout" },
                        { icon: Lock, label: "SSL Encrypted" },
                        { icon: CreditCard, label: "Stripe Protected" },
                      ].map(({ icon: Icon, label }) => (
                        <div key={label} className="flex items-center gap-1.5 text-muted-foreground">
                          <Icon className="w-4 h-4 text-icon-secure" />
                          <span className="text-[11px] font-body font-medium">{label}</span>
                        </div>
                      ))}
                    </motion.div>

                    <div className="flex justify-between pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={prevStep}
                        className="rounded-xl"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" /> Back
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ══════════════════════════════════════════════════
                Right: Order Summary — UNCHANGED
               ══════════════════════════════════════════════════ */}
            <div>
              <div className="bg-card border border-border rounded-lg p-6 sticky top-24">
                <h2 className="font-display text-lg font-semibold mb-4">
                  Order Summary
                </h2>

                <div className="space-y-3 mb-6">
                  {items.map((item) => {
                    const price = getEffectivePrice(item.product);
                    return (
                      <div
                        key={`${item.product.id}-${item.variantLabel}`}
                        className="flex gap-3"
                      >
                        <img
                          src={getFirstImage(item.product.images)}
                          alt=""
                          className="w-14 h-14 rounded object-cover bg-muted shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-body font-medium truncate">
                            {item.product.name}
                          </p>
                          {item.variantLabel && (
                            <p className="text-xs text-muted-foreground">
                              {item.variantLabel}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center border border-input rounded">
                              <button
                                type="button"
                                onClick={() => {
                                  if (item.quantity === 1 && items.length === 1) {
                                    setLastItemAction(() => () => updateQuantity(item.product.id, 0, item.variantLabel));
                                    return;
                                  }
                                  updateQuantity(item.product.id, item.quantity - 1, item.variantLabel);
                                }}
                                className="p-1 hover:bg-secondary transition-colors"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="px-2 text-xs font-body font-medium">{item.quantity}</span>
                              <button
                                type="button"
                                onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.variantLabel)}
                                className="p-1 hover:bg-secondary transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                if (items.length === 1) {
                                  setLastItemAction(() => () => removeItem(item.product.id, item.variantLabel));
                                  return;
                                }
                                removeItem(item.product.id, item.variantLabel);
                              }}
                              className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                            <span className="text-xs text-muted-foreground font-body ml-auto">
                              £{price.toFixed(2)} ea
                            </span>
                          </div>
                        </div>
                        <p className="text-sm font-body font-bold self-start">
                          £{(price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <FreeShippingBar subtotal={subtotal} />

                {/* Discount Code */}
                <div className="border-t border-border pt-4 pb-2">
                  <p className="text-sm font-body font-medium mb-2">Discount Code</p>
                  {discountApplied ? (
                    <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded px-3 py-2">
                      <div>
                        <span className="text-sm font-body font-semibold text-primary">{discountApplied.code}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {discountApplied.type === "percentage"
                            ? `${discountApplied.value}% off`
                            : `£${discountApplied.value.toFixed(2)} off`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setDiscountApplied(null);
                          setDiscountCode("");
                        }}
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter code"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        maxLength={50}
                        className="flex-1 h-9 text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={applyDiscount}
                        disabled={discountLoading || !discountCode.trim()}
                        className="h-9 px-4"
                      >
                        {discountLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex justify-between text-sm font-body">
                    <span className="text-muted-foreground">
                      Subtotal ({totalItems} items)
                    </span>
                    <span>£{subtotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex justify-between text-sm font-body">
                      <span className="text-icon-success">Discount</span>
                      <span className="text-icon-success">-£{discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-body">
                    <span className="text-muted-foreground">
                      Shipping{!isFreeShipping && <span className="text-xs ml-1">({shippingLabel})</span>}
                    </span>
                    <span>
                      {isFreeShipping ? (
                        <span className="text-icon-success font-medium">Free</span>
                      ) : (
                        `£${shipping.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  {!isFreeShipping && freeThreshold > 0 && (
                    <p className="text-xs text-muted-foreground font-body">
                      Free shipping on orders over £{freeThreshold.toFixed(0)}
                    </p>
                  )}
                  <div className="flex justify-between text-base font-body font-bold pt-2 border-t border-border">
                    <span>Total</span>
                    <span className="text-primary">£{total.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full mt-6"
                  disabled={submitting || currentStep < 2 || !canProceedStep0 || !canProceedStep1}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting to payment…
                    </>
                  ) : currentStep < 2 ? (
                    "Complete all steps to pay"
                  ) : (
                    <>
                      <GlossyIcon name="lock" size={18} className="mr-1" />
                      Pay · £{total.toFixed(2)}
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center mt-3 font-body">
                  You'll be redirected to Stripe for secure payment
                </p>

                {/* Upsell */}
                <CartUpsell compact />
              </div>
            </div>
          </div>
        </form>
      </div>

      <Footer />

      <AlertDialog open={!!lastItemAction} onOpenChange={(open) => !open && setLastItemAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove last item?</AlertDialogTitle>
            <AlertDialogDescription>
              This is the last item in your cart. Removing it will empty your cart and take you back to the shop.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Item</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                lastItemAction?.();
                setLastItemAction(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Checkout;
